import type {
  JsonValue,
  RenderMode,
  RenderVariant,
} from "../contracts/media-binding.js";
import { BAKEOFF_RENDER_MODES, RENDER_MODES } from "../contracts/media-binding.js";
import type {
  CaptionToken,
  CaptionTokenRole,
  HeadlineTemplate,
  LookTreatment,
  CameraMotionPreset,
  TransitionPreset,
  StickerOption,
  SfxOption,
  SourcePack,
} from "../contracts/source-pack.js";
import { SOURCE_PACK_SCHEMA_VERSION } from "../contracts/source-pack.js";
import {
  allHeadlineExpansionChoices,
  allHeadlineExpansions,
  headlineTemplateExpansionsAreGrammatical,
} from "../edit/headline-templates.js";
import {
  isObject,
  requireArray,
  requireBoolean,
  requireInt,
  requireMilli,
  requireNonEmptyString,
  requireObject,
  requireStringArray,
  uniqueIds,
  ValidationError,
} from "./helpers.js";
import { captionMatchesGrammar } from "./validate-edit-graph.js";

const CONTENT_RATINGS = new Set(["everyone", "teen", "mature"]);
const ENTITY_KINDS = new Set(["person", "animal", "object", "place", "other"]);
const TOKEN_ROLES = new Set<CaptionTokenRole>([
  "subject",
  "object",
  "action",
  "motive",
  "qualifier",
  "hook",
  "other",
]);
const ASSET_KINDS = new Set(["visual", "audio", "sticker", "font", "effect", "other"]);
const ASSET_SOURCE_KINDS = new Set(["file", "procedural"]);
const RENDER_MODE_SET = new Set<string>(RENDER_MODES);

/**
 * Normalized POSIX public-relative path for `sourceKind: "file"`.
 * Rejects schemes, absolute/drive paths, backslashes, empty/`.`/`..` segments,
 * query strings, and fragments.
 */
export function isValidPublicRelativeAssetPath(path: string): boolean {
  if (typeof path !== "string" || path.length === 0) return false;
  if (path.includes("\\")) return false;
  if (path.startsWith("/")) return false;
  if (path.includes("://") || /^[A-Za-z]:/.test(path)) return false;
  if (path.includes("?") || path.includes("#")) return false;
  if (path.includes(":")) return false; // schemes / Windows drive leftovers
  const segments = path.split("/");
  if (segments.some((s) => s === "" || s === "." || s === "..")) return false;
  return true;
}

function isJsonValue(value: unknown): value is JsonValue {
  if (value === null) return true;
  const t = typeof value;
  if (t === "string" || t === "boolean") return true;
  if (t === "number") return Number.isFinite(value as number);
  if (Array.isArray(value)) return value.every(isJsonValue);
  if (isObject(value)) return Object.values(value).every(isJsonValue);
  return false;
}

function requireJsonObject(path: string, value: unknown): { [key: string]: JsonValue } {
  const obj = requireObject(path, value);
  for (const [key, child] of Object.entries(obj)) {
    if (!isJsonValue(child)) {
      throw new ValidationError(`${path}.${key}`, "expected JSON-safe value");
    }
  }
  return obj as { [key: string]: JsonValue };
}

function validateRenderVariant(
  path: string,
  raw: unknown,
  beatIds: Set<string>,
  assetIds: Set<string>,
  canonicalOrder: string[],
): RenderVariant {
  const v = requireObject(path, raw);
  const id = requireNonEmptyString(`${path}.id`, v.id);
  const modeRaw = requireNonEmptyString(`${path}.mode`, v.mode);
  if (!RENDER_MODE_SET.has(modeRaw)) {
    throw new ValidationError(`${path}.mode`, `unsupported render mode "${modeRaw}"`);
  }
  const mode = modeRaw as RenderMode;
  const label = requireNonEmptyString(`${path}.label`, v.label);
  const requiredAssetIds = requireStringArray(`${path}.requiredAssetIds`, v.requiredAssetIds);
  uniqueIds(`${path}.requiredAssetIds`, requiredAssetIds);
  for (const assetId of requiredAssetIds) {
    if (!assetIds.has(assetId)) {
      throw new ValidationError(`${path}.requiredAssetIds`, `unknown asset "${assetId}"`);
    }
  }

  const bindingsRaw = requireArray(`${path}.beatBindings`, v.beatBindings);
  if (bindingsRaw.length === 0) {
    throw new ValidationError(`${path}.beatBindings`, "at least one binding required");
  }

  const boundBeats = new Set<string>();
  const bindingIds: string[] = [];
  const beatBindings = bindingsRaw.map((bindingRaw, i) => {
    const bPath = `${path}.beatBindings[${i}]`;
    const b = requireObject(bPath, bindingRaw);
    const bindingId = requireNonEmptyString(`${bPath}.bindingId`, b.bindingId);
    bindingIds.push(bindingId);

    const beatId = requireNonEmptyString(`${bPath}.beatId`, b.beatId);
    if (!beatIds.has(beatId)) {
      throw new ValidationError(`${bPath}.beatId`, `unknown beat "${beatId}"`);
    }
    boundBeats.add(beatId);

    const assetId = requireNonEmptyString(`${bPath}.assetId`, b.assetId);
    if (!assetIds.has(assetId)) {
      throw new ValidationError(`${bPath}.assetId`, `unknown asset "${assetId}"`);
    }
    if (!requiredAssetIds.includes(assetId)) {
      throw new ValidationError(
        `${bPath}.assetId`,
        `asset "${assetId}" must appear in requiredAssetIds`,
      );
    }

    const sourceStartTick = requireInt(`${bPath}.sourceStartTick`, b.sourceStartTick, 0);
    const sourceEndTick = requireInt(
      `${bPath}.sourceEndTick`,
      b.sourceEndTick,
      sourceStartTick + 1,
    );

    const binding: RenderVariant["beatBindings"][number] = {
      bindingId,
      beatId,
      assetId,
      sourceStartTick,
      sourceEndTick,
    };
    if (b.layerId !== undefined) {
      binding.layerId = requireNonEmptyString(`${bPath}.layerId`, b.layerId);
    }
    if (b.animationId !== undefined) {
      binding.animationId = requireNonEmptyString(`${bPath}.animationId`, b.animationId);
    }
    if (b.clipId !== undefined) {
      binding.clipId = requireNonEmptyString(`${bPath}.clipId`, b.clipId);
    }
    return binding;
  });

  uniqueIds(`${path}.beatBindings.bindingId`, bindingIds);

  for (const beatId of canonicalOrder) {
    if (!boundBeats.has(beatId)) {
      throw new ValidationError(
        `${path}.beatBindings`,
        `missing binding for beat "${beatId}" (at least one required)`,
      );
    }
  }

  return {
    id,
    mode,
    label,
    requiredAssetIds,
    beatBindings,
    config: requireJsonObject(`${path}.config`, v.config),
  };
}

/** Validates a SourcePack against schema version 1. Malformed input → ValidationError only. */
export function validateSourcePack(raw: unknown): SourcePack {
  const root = requireObject("$", raw);

  if (root.schemaVersion !== SOURCE_PACK_SCHEMA_VERSION) {
    throw new ValidationError(
      "schemaVersion",
      `unsupported version ${String(root.schemaVersion)}`,
    );
  }

  const id = requireNonEmptyString("id", root.id);
  const version = requireNonEmptyString("version", root.version);
  const title = requireNonEmptyString("title", root.title);
  const contentRating = requireNonEmptyString("contentRating", root.contentRating);
  if (!CONTENT_RATINGS.has(contentRating)) {
    throw new ValidationError("contentRating", `invalid rating "${contentRating}"`);
  }
  const tickRate = requireInt("tickRate", root.tickRate, 1);

  const licenseObj = requireObject("license", root.license);
  const license = {
    summary: requireNonEmptyString("license.summary", licenseObj.summary),
    attributions: requireStringArray("license.attributions", licenseObj.attributions),
    packLicense: requireNonEmptyString("license.packLicense", licenseObj.packLicense),
  };

  const entities = requireArray("entities", root.entities).map((entityRaw, i) => {
    const path = `entities[${i}]`;
    const e = requireObject(path, entityRaw);
    const kind = requireNonEmptyString(`${path}.kind`, e.kind);
    if (!ENTITY_KINDS.has(kind)) {
      throw new ValidationError(`${path}.kind`, `invalid kind "${kind}"`);
    }
    return {
      id: requireNonEmptyString(`${path}.id`, e.id),
      name: requireNonEmptyString(`${path}.name`, e.name),
      kind: kind as SourcePack["entities"][number]["kind"],
      contextCarrier: requireBoolean(`${path}.contextCarrier`, e.contextCarrier),
      description: requireNonEmptyString(`${path}.description`, e.description),
    };
  });
  uniqueIds(
    "entities",
    entities.map((e) => e.id),
  );
  const entityIds = new Set(entities.map((e) => e.id));

  const scoringTags = requireArray("scoringTags", root.scoringTags).map((tagRaw, i) => {
    const path = `scoringTags[${i}]`;
    const t = requireObject(path, tagRaw);
    return {
      id: requireNonEmptyString(`${path}.id`, t.id),
      label: requireNonEmptyString(`${path}.label`, t.label),
      reachMilli: requireMilli(`${path}.reachMilli`, t.reachMilli, -1000, 1000),
      contextLossMilli: requireMilli(`${path}.contextLossMilli`, t.contextLossMilli, 0, 1000),
    };
  });
  uniqueIds(
    "scoringTags",
    scoringTags.map((t) => t.id),
  );
  const scoringTagIds = new Set(scoringTags.map((t) => t.id));

  const beatsRaw = requireArray("beats", root.beats);
  if (beatsRaw.length === 0) {
    throw new ValidationError("beats", "at least one beat required");
  }
  const beats = beatsRaw.map((beatRaw, i) => {
    const path = `beats[${i}]`;
    const b = requireObject(path, beatRaw);
    const startTick = requireInt(`${path}.startTick`, b.startTick, 0);
    const endTick = requireInt(`${path}.endTick`, b.endTick, startTick + 1);
    const beatEntityIds = requireStringArray(`${path}.entityIds`, b.entityIds);
    for (const entityId of beatEntityIds) {
      if (!entityIds.has(entityId)) {
        throw new ValidationError(`${path}.entityIds`, `unknown entity "${entityId}"`);
      }
    }
    const dialogue = requireArray(`${path}.dialogue`, b.dialogue).map((line, di) =>
      requireNonEmptyString(`${path}.dialogue[${di}]`, line),
    );
    const tagIds = requireStringArray(`${path}.scoringTagIds`, b.scoringTagIds);
    for (const tagId of tagIds) {
      if (!scoringTagIds.has(tagId)) {
        throw new ValidationError(`${path}.scoringTagIds`, `unknown tag "${tagId}"`);
      }
    }
    return {
      id: requireNonEmptyString(`${path}.id`, b.id),
      label: requireNonEmptyString(`${path}.label`, b.label),
      startTick,
      endTick,
      entityIds: beatEntityIds,
      dialogue,
      contextCritical: requireBoolean(`${path}.contextCritical`, b.contextCritical),
      intensityMilli: requireMilli(`${path}.intensityMilli`, b.intensityMilli, 0, 1000),
      hookMilli: requireMilli(`${path}.hookMilli`, b.hookMilli, 0, 1000),
      scoringTagIds: tagIds,
    };
  });
  uniqueIds(
    "beats",
    beats.map((b) => b.id),
  );
  const beatIds = new Set(beats.map((b) => b.id));

  const canonicalObj = requireObject("canonicalRecord", root.canonicalRecord);
  const beatOrder = requireStringArray("canonicalRecord.beatOrder", canonicalObj.beatOrder);
  if (beatOrder.length !== beats.length) {
    throw new ValidationError(
      "canonicalRecord.beatOrder",
      "must list every beat exactly once",
    );
  }
  uniqueIds("canonicalRecord.beatOrder", beatOrder);
  for (const beatId of beatOrder) {
    if (!beatIds.has(beatId)) {
      throw new ValidationError("canonicalRecord.beatOrder", `unknown beat "${beatId}"`);
    }
  }
  for (const beat of beats) {
    if (!beatOrder.includes(beat.id)) {
      throw new ValidationError("canonicalRecord.beatOrder", `missing beat "${beat.id}"`);
    }
  }
  const canonicalRecord = {
    title: requireNonEmptyString("canonicalRecord.title", canonicalObj.title),
    summary: requireNonEmptyString("canonicalRecord.summary", canonicalObj.summary),
    beatOrder,
  };

  const claims = requireArray("claims", root.claims).map((claimRaw, i) => {
    const path = `claims[${i}]`;
    const c = requireObject(path, claimRaw);
    const claimId = requireNonEmptyString(`${path}.id`, c.id);
    const supportingBeatIds = requireStringArray(`${path}.supportingBeatIds`, c.supportingBeatIds);
    const contradictingBeatIds = requireStringArray(
      `${path}.contradictingBeatIds`,
      c.contradictingBeatIds,
    );
    for (const beatId of [...supportingBeatIds, ...contradictingBeatIds]) {
      if (!beatIds.has(beatId)) {
        throw new ValidationError(path, `unknown beat "${beatId}"`);
      }
    }
    const implyingTokenGroups = requireArray(`${path}.implyingTokenGroups`, c.implyingTokenGroups).map(
      (groupRaw, gi) => {
        const group = requireStringArray(`${path}.implyingTokenGroups[${gi}]`, groupRaw);
        if (group.length === 0) {
          throw new ValidationError(`${path}.implyingTokenGroups[${gi}]`, "group must not be empty");
        }
        return group;
      },
    );
    return {
      id: claimId,
      statement: requireNonEmptyString(`${path}.statement`, c.statement),
      supportingBeatIds,
      contradictingBeatIds,
      implyingTokenGroups,
      driftWeightMilli: requireMilli(`${path}.driftWeightMilli`, c.driftWeightMilli, 0, 1000),
    };
  });
  uniqueIds(
    "claims",
    claims.map((c) => c.id),
  );
  const claimIds = new Set(claims.map((c) => c.id));

  const vocabObj = requireObject("captionVocabulary", root.captionVocabulary);
  const tokens = requireArray("captionVocabulary.tokens", vocabObj.tokens).map((tokenRaw, i) => {
    const path = `captionVocabulary.tokens[${i}]`;
    const t = requireObject(path, tokenRaw);
    const role = requireNonEmptyString(`${path}.role`, t.role);
    if (!TOKEN_ROLES.has(role as CaptionTokenRole)) {
      throw new ValidationError(`${path}.role`, `invalid role "${role}"`);
    }
    const tokenClaimIds = requireStringArray(`${path}.claimIds`, t.claimIds);
    for (const claimId of tokenClaimIds) {
      if (!claimIds.has(claimId)) {
        throw new ValidationError(`${path}.claimIds`, `unknown claim "${claimId}"`);
      }
    }
    return {
      id: requireNonEmptyString(`${path}.id`, t.id),
      text: requireNonEmptyString(`${path}.text`, t.text),
      role: role as CaptionTokenRole,
      claimIds: tokenClaimIds,
      intensityMilli: requireMilli(`${path}.intensityMilli`, t.intensityMilli, 0, 1000),
    };
  });
  uniqueIds(
    "captionVocabulary.tokens",
    tokens.map((t) => t.id),
  );
  const tokenIds = new Set(tokens.map((t) => t.id));

  for (const claim of claims) {
    claim.implyingTokenGroups.forEach((group, gi) => {
      group.forEach((tokenId, ti) => {
        if (!tokenIds.has(tokenId)) {
          throw new ValidationError(
            `claims.${claim.id}.implyingTokenGroups[${gi}][${ti}]`,
            `unknown token "${tokenId}"`,
          );
        }
      });
    });
  }

  const grammarObj = requireObject("captionGrammar", root.captionGrammar);
  const maxTokens = requireInt("captionGrammar.maxTokens", grammarObj.maxTokens, 1);
  const rulesRaw = requireArray("captionGrammar.rules", grammarObj.rules);
  if (rulesRaw.length === 0) {
    throw new ValidationError("captionGrammar.rules", "at least one rule required");
  }
  const rules = rulesRaw.map((ruleRaw, i) => {
    const path = `captionGrammar.rules[${i}]`;
    const r = requireObject(path, ruleRaw);
    const rolesRaw = requireArray(`${path}.roles`, r.roles);
    if (rolesRaw.length === 0) {
      throw new ValidationError(`${path}.roles`, "at least one role required");
    }
    if (rolesRaw.length > maxTokens) {
      throw new ValidationError(`${path}.roles`, "role count exceeds maxTokens");
    }
    const roles = rolesRaw.map((role, ri) => {
      const roleStr = requireNonEmptyString(`${path}.roles[${ri}]`, role);
      if (!TOKEN_ROLES.has(roleStr as CaptionTokenRole)) {
        throw new ValidationError(`${path}.roles[${ri}]`, `invalid role "${roleStr}"`);
      }
      return roleStr as CaptionTokenRole;
    });
    return {
      id: requireNonEmptyString(`${path}.id`, r.id),
      roles,
    };
  });
  uniqueIds(
    "captionGrammar.rules",
    rules.map((r) => r.id),
  );

  const ambiguityNotes = requireArray("ambiguityNotes", root.ambiguityNotes).map((noteRaw, i) => {
    const path = `ambiguityNotes[${i}]`;
    const n = requireObject(path, noteRaw);
    const relatedBeatIds = requireStringArray(`${path}.relatedBeatIds`, n.relatedBeatIds);
    const relatedClaimIds = requireStringArray(`${path}.relatedClaimIds`, n.relatedClaimIds);
    for (const beatId of relatedBeatIds) {
      if (!beatIds.has(beatId)) {
        throw new ValidationError(`${path}.relatedBeatIds`, `unknown beat "${beatId}"`);
      }
    }
    for (const claimId of relatedClaimIds) {
      if (!claimIds.has(claimId)) {
        throw new ValidationError(`${path}.relatedClaimIds`, `unknown claim "${claimId}"`);
      }
    }
    return {
      id: requireNonEmptyString(`${path}.id`, n.id),
      text: requireNonEmptyString(`${path}.text`, n.text),
      relatedBeatIds,
      relatedClaimIds,
    };
  });
  uniqueIds(
    "ambiguityNotes",
    ambiguityNotes.map((n) => n.id),
  );

  const assets = requireArray("assets", root.assets).map((assetRaw, i) => {
    const path = `assets[${i}]`;
    const a = requireObject(path, assetRaw);
    const kind = requireNonEmptyString(`${path}.kind`, a.kind);
    if (!ASSET_KINDS.has(kind)) {
      throw new ValidationError(`${path}.kind`, `invalid kind "${kind}"`);
    }
    const sourceKindRaw = a.sourceKind;
    const sourceKind =
      sourceKindRaw === undefined
        ? ("file" as const)
        : (requireNonEmptyString(`${path}.sourceKind`, sourceKindRaw) as string);
    if (!ASSET_SOURCE_KINDS.has(sourceKind)) {
      throw new ValidationError(`${path}.sourceKind`, `invalid sourceKind "${sourceKind}"`);
    }
    const sourceUrl = requireNonEmptyString(`${path}.sourceUrl`, a.sourceUrl);
    let assetPath: string;
    if (sourceKind === "procedural") {
      if (typeof a.path !== "string") {
        throw new ValidationError(`${path}.path`, "procedural assets require string path (empty)");
      }
      if (a.path !== "") {
        throw new ValidationError(
          `${path}.path`,
          "procedural assets must use empty path (no packed placeholder binary)",
        );
      }
      if (!sourceUrl.startsWith("repo:")) {
        throw new ValidationError(
          `${path}.sourceUrl`,
          "procedural assets require a stable repo: sourceUrl",
        );
      }
      assetPath = "";
    } else {
      assetPath = requireNonEmptyString(`${path}.path`, a.path);
      if (!isValidPublicRelativeAssetPath(assetPath)) {
        throw new ValidationError(
          `${path}.path`,
          "file assets require a normalized POSIX public-relative path (no scheme, leading slash, backslash, . or .. segments, query, or fragment)",
        );
      }
    }
    let fallbackPath: string | undefined;
    if (a.fallbackPath !== undefined && a.fallbackPath !== null) {
      if (sourceKind === "procedural") {
        throw new ValidationError(
          `${path}.fallbackPath`,
          "procedural assets must not declare fallbackPath",
        );
      }
      fallbackPath = requireNonEmptyString(`${path}.fallbackPath`, a.fallbackPath);
      if (!isValidPublicRelativeAssetPath(fallbackPath)) {
        throw new ValidationError(
          `${path}.fallbackPath`,
          "fallbackPath must be a normalized POSIX public-relative path",
        );
      }
      if (fallbackPath === assetPath) {
        throw new ValidationError(`${path}.fallbackPath`, "fallbackPath must differ from path");
      }
    }
    return {
      id: requireNonEmptyString(`${path}.id`, a.id),
      kind: kind as SourcePack["assets"][number]["kind"],
      path: assetPath,
      ...(fallbackPath !== undefined ? { fallbackPath } : {}),
      license: requireNonEmptyString(`${path}.license`, a.license),
      author: requireNonEmptyString(`${path}.author`, a.author),
      sourceUrl,
      downloadedAt: requireNonEmptyString(`${path}.downloadedAt`, a.downloadedAt),
      modifications: requireNonEmptyString(`${path}.modifications`, a.modifications),
      tags: requireArray(`${path}.tags`, a.tags).map((tag, ti) =>
        requireNonEmptyString(`${path}.tags[${ti}]`, tag),
      ),
      ...(sourceKindRaw !== undefined || sourceKind === "procedural"
        ? { sourceKind: sourceKind as "file" | "procedural" }
        : {}),
    };
  });
  uniqueIds(
    "assets",
    assets.map((a) => a.id),
  );
  const audioFilePaths = assets
    .filter((a) => a.kind === "audio" && (a.sourceKind ?? "file") === "file")
    .flatMap((a) => (a.fallbackPath ? [a.path, a.fallbackPath] : [a.path]));
  if (new Set(audioFilePaths).size !== audioFilePaths.length) {
    throw new ValidationError("assets", "duplicate audio file paths are not allowed");
  }
  const assetIds = new Set(assets.map((a) => a.id));

  const variantsRaw = requireArray("renderVariants", root.renderVariants);
  if (variantsRaw.length === 0) {
    throw new ValidationError("renderVariants", "at least one render variant required");
  }
  const renderVariants = variantsRaw.map((variantRaw, i) =>
    validateRenderVariant(`renderVariants[${i}]`, variantRaw, beatIds, assetIds, beatOrder),
  );
  uniqueIds(
    "renderVariants",
    renderVariants.map((v) => v.id),
  );

  const audienceObj = requireObject("audience", root.audience);
  const audience = {
    id: requireNonEmptyString("audience.id", audienceObj.id),
    label: requireNonEmptyString("audience.label", audienceObj.label),
    preferredIntensityMilli: requireMilli(
      "audience.preferredIntensityMilli",
      audienceObj.preferredIntensityMilli,
      0,
      1000,
    ),
    noveltyWeightMilli: requireMilli(
      "audience.noveltyWeightMilli",
      audienceObj.noveltyWeightMilli,
      0,
      1000,
    ),
    credibilitySensitivityMilli: requireMilli(
      "audience.credibilitySensitivityMilli",
      audienceObj.credibilitySensitivityMilli,
      0,
      1000,
    ),
  };

  const trends = requireArray("trends", root.trends).map((trendRaw, i) => {
    const path = `trends[${i}]`;
    const t = requireObject(path, trendRaw);
    const matchingTagIds = requireStringArray(`${path}.matchingTagIds`, t.matchingTagIds);
    if (matchingTagIds.length === 0) {
      throw new ValidationError(`${path}.matchingTagIds`, "at least one tag required");
    }
    for (const tagId of matchingTagIds) {
      if (!scoringTagIds.has(tagId)) {
        throw new ValidationError(`${path}.matchingTagIds`, `unknown tag "${tagId}"`);
      }
    }
    return {
      id: requireNonEmptyString(`${path}.id`, t.id),
      label: requireNonEmptyString(`${path}.label`, t.label),
      reachBoostMilli: requireMilli(`${path}.reachBoostMilli`, t.reachBoostMilli, 0, 5000),
      matchingTagIds,
    };
  });
  uniqueIds(
    "trends",
    trends.map((t) => t.id),
  );

  const exportObj = requireObject("exportSafety", root.exportSafety);
  const exportSafety = {
    fictionalSubjectsOnly: requireBoolean(
      "exportSafety.fictionalSubjectsOnly",
      exportObj.fictionalSubjectsOnly,
    ),
    allowsIdentifiablePeople: requireBoolean(
      "exportSafety.allowsIdentifiablePeople",
      exportObj.allowsIdentifiablePeople,
    ),
    contentWarnings: requireStringArray("exportSafety.contentWarnings", exportObj.contentWarnings),
    requiredWatermark: requireNonEmptyString(
      "exportSafety.requiredWatermark",
      exportObj.requiredWatermark,
    ),
    attributionRequired: requireBoolean(
      "exportSafety.attributionRequired",
      exportObj.attributionRequired,
    ),
  };

  let castSlots: SourcePack["castSlots"] = [];
  if (root.castSlots !== undefined) {
    const slotsRaw = requireArray("castSlots", root.castSlots);
    castSlots = slotsRaw.map((slotRaw, i) => {
      const path = `castSlots[${i}]`;
      const s = requireObject(path, slotRaw);
      const slotId = requireNonEmptyString(`${path}.id`, s.id);
      const entityId = requireNonEmptyString(`${path}.entityId`, s.entityId);
      if (!entityIds.has(entityId)) {
        throw new ValidationError(`${path}.entityId`, `unknown entity "${entityId}"`);
      }
      const defaultVariantId = requireNonEmptyString(
        `${path}.defaultVariantId`,
        s.defaultVariantId,
      );
      const variantsRaw = requireArray(`${path}.variants`, s.variants);
      if (variantsRaw.length === 0) {
        throw new ValidationError(`${path}.variants`, "at least one variant required");
      }
      const variants = variantsRaw.map((variantRaw, vi) => {
        const vPath = `${path}.variants[${vi}]`;
        const v = requireObject(vPath, variantRaw);
        const visualAssetId = requireNonEmptyString(`${vPath}.visualAssetId`, v.visualAssetId);
        const asset = assets.find((a) => a.id === visualAssetId);
        if (!asset) {
          throw new ValidationError(`${vPath}.visualAssetId`, `unknown asset "${visualAssetId}"`);
        }
        if (asset.kind !== "visual") {
          throw new ValidationError(
            `${vPath}.visualAssetId`,
            'cast variant asset must have kind "visual"',
          );
        }
        return {
          id: requireNonEmptyString(`${vPath}.id`, v.id),
          label: requireNonEmptyString(`${vPath}.label`, v.label),
          sourceFaithful: requireBoolean(`${vPath}.sourceFaithful`, v.sourceFaithful),
          visualAssetId,
          presentationReachMilli: requireMilli(
            `${vPath}.presentationReachMilli`,
            v.presentationReachMilli,
            -1000,
            1000,
          ),
          provenanceRiskMilli: requireMilli(
            `${vPath}.provenanceRiskMilli`,
            v.provenanceRiskMilli,
            0,
            1000,
          ),
          tags: requireArray(`${vPath}.tags`, v.tags).map((tag, ti) =>
            requireNonEmptyString(`${vPath}.tags[${ti}]`, tag),
          ),
        };
      });
      uniqueIds(
        `${path}.variants`,
        variants.map((v) => v.id),
      );
      const defaultVariant = variants.find((v) => v.id === defaultVariantId);
      if (!defaultVariant) {
        throw new ValidationError(
          `${path}.defaultVariantId`,
          `missing default variant "${defaultVariantId}"`,
        );
      }
      const faithfulCount = variants.filter((v) => v.sourceFaithful).length;
      if (faithfulCount !== 1 || !defaultVariant.sourceFaithful) {
        throw new ValidationError(
          `${path}.sourceFaithful`,
          "exactly one sourceFaithful variant required and it must be the default",
        );
      }
      if (defaultVariant.provenanceRiskMilli !== 0 || defaultVariant.presentationReachMilli !== 0) {
        throw new ValidationError(
          `${path}.variants`,
          "source-faithful default must have zero presentationReachMilli and provenanceRiskMilli",
        );
      }
      return {
        id: slotId,
        label: requireNonEmptyString(`${path}.label`, s.label),
        entityId,
        defaultVariantId,
        variants,
      };
    });
    uniqueIds(
      "castSlots",
      castSlots.map((s) => s.id),
    );
  }

  const headlineTemplates = validateHeadlineTemplates(
    root.headlineTemplates,
    tokenIds,
    { rules, maxTokens },
    tokens,
  );
  const lookTreatments = validateLookTreatments(root.lookTreatments);
  const cameraMotions = validateCameraMotions(root.cameraMotions);
  const transitionPresets = validateTransitionPresets(root.transitionPresets);
  const stickerOptions = validateStickerOptions(root.stickerOptions, beatIds, assetIds, assets);
  const sourceEndTick = beats.reduce((max, b) => Math.max(max, b.endTick), 0);
  const sfxOptions = validateSfxOptions(
    root.sfxOptions,
    assetIds,
    assets,
    sourceEndTick,
  );

  return {
    schemaVersion: SOURCE_PACK_SCHEMA_VERSION,
    id,
    version,
    title,
    contentRating: contentRating as SourcePack["contentRating"],
    license,
    canonicalRecord,
    beats,
    entities,
    claims,
    ambiguityNotes,
    captionVocabulary: { tokens },
    captionGrammar: { rules, maxTokens },
    assets,
    renderVariants,
    castSlots,
    headlineTemplates,
    lookTreatments,
    cameraMotions,
    transitionPresets,
    stickerOptions,
    sfxOptions,
    scoringTags,
    audience,
    trends,
    exportSafety,
    tickRate,
  };
}

/** Pick a render variant by mode (first match) or exact id. */
export function selectRenderVariant(
  pack: SourcePack,
  modeOrId: RenderMode | string,
): RenderVariant {
  const byId = pack.renderVariants.find((v) => v.id === modeOrId);
  if (byId) return byId;
  const byMode = pack.renderVariants.find((v) => v.mode === modeOrId);
  if (byMode) return byMode;
  throw new ValidationError("renderVariants", `no variant for "${modeOrId}"`);
}

/**
 * Bakeoff conformance: sample packs used to compare all three POCs must ship
 * one variant for each render mode. Not required of ordinary SourcePacks.
 */
export function assertBakeoffRenderModeCoverage(pack: SourcePack): void {
  const modesPresent = new Set(pack.renderVariants.map((v) => v.mode));
  for (const mode of BAKEOFF_RENDER_MODES) {
    if (!modesPresent.has(mode)) {
      throw new ValidationError(
        "renderVariants",
        `bakeoff pack missing variant for mode "${mode}"`,
      );
    }
  }
}

function validateHeadlineTemplates(
  raw: unknown,
  tokenIds: Set<string>,
  grammar: SourcePack["captionGrammar"],
  tokens: CaptionToken[],
): HeadlineTemplate[] {
  if (raw === undefined) return [];
  const list = requireArray("headlineTemplates", raw);
  const packStub = {
    captionVocabulary: { tokens },
    captionGrammar: grammar,
  } as SourcePack;
  const templates = list.map((templateRaw, i) => {
    const path = `headlineTemplates[${i}]`;
    const t = requireObject(path, templateRaw);
    const segmentsRaw = requireArray(`${path}.segments`, t.segments);
    if (segmentsRaw.length === 0) {
      throw new ValidationError(`${path}.segments`, "at least one segment required");
    }
    const segments = segmentsRaw.map((segRaw, si) => {
      const sPath = `${path}.segments[${si}]`;
      const s = requireObject(sPath, segRaw);
      const kind = requireNonEmptyString(`${sPath}.kind`, s.kind);
      if (kind === "fixed") {
        const fixedTokenIds = requireStringArray(`${sPath}.tokenIds`, s.tokenIds);
        if (fixedTokenIds.length === 0) {
          throw new ValidationError(`${sPath}.tokenIds`, "fixed segment needs tokens");
        }
        for (const tokenId of fixedTokenIds) {
          if (!tokenIds.has(tokenId)) {
            throw new ValidationError(`${sPath}.tokenIds`, `unknown token "${tokenId}"`);
          }
        }
        return {
          kind: "fixed" as const,
          id: requireNonEmptyString(`${sPath}.id`, s.id),
          tokenIds: fixedTokenIds,
        };
      }
      if (kind === "slot") {
        const optionsRaw = requireArray(`${sPath}.options`, s.options);
        if (optionsRaw.length === 0) {
          throw new ValidationError(`${sPath}.options`, "at least one option required");
        }
        const options = optionsRaw.map((optRaw, oi) => {
          const oPath = `${sPath}.options[${oi}]`;
          const o = requireObject(oPath, optRaw);
          const optionTokenIds = requireStringArray(`${oPath}.tokenIds`, o.tokenIds);
          if (optionTokenIds.length === 0) {
            throw new ValidationError(`${oPath}.tokenIds`, "option needs tokens");
          }
          for (const tokenId of optionTokenIds) {
            if (!tokenIds.has(tokenId)) {
              throw new ValidationError(`${oPath}.tokenIds`, `unknown token "${tokenId}"`);
            }
          }
          return {
            id: requireNonEmptyString(`${oPath}.id`, o.id),
            tokenIds: optionTokenIds,
          };
        });
        uniqueIds(
          `${sPath}.options`,
          options.map((o) => o.id),
        );
        const defaultOptionId = requireNonEmptyString(
          `${sPath}.defaultOptionId`,
          s.defaultOptionId,
        );
        if (!options.some((o) => o.id === defaultOptionId)) {
          throw new ValidationError(
            `${sPath}.defaultOptionId`,
            `missing default option "${defaultOptionId}"`,
          );
        }
        if (options.length < 2) {
          throw new ValidationError(
            `${sPath}.options`,
            "headline slot must have at least 2 options (use fixed segments for non-swappable text)",
          );
        }
        return {
          kind: "slot" as const,
          id: requireNonEmptyString(`${sPath}.id`, s.id),
          defaultOptionId,
          options,
        };
      }
      throw new ValidationError(`${sPath}.kind`, `invalid segment kind "${kind}"`);
    });
    uniqueIds(
      `${path}.segments`,
      segments.map((s) => s.id),
    );
    const slotCount = segments.filter((s) => s.kind === "slot").length;
    if (slotCount < 1 || slotCount > 3) {
      throw new ValidationError(
        `${path}.segments`,
        "headline template must have 1–3 editable slots",
      );
    }
    const template: HeadlineTemplate = {
      id: requireNonEmptyString(`${path}.id`, t.id),
      label: requireNonEmptyString(`${path}.label`, t.label),
      segments,
    };
    const expansions = allHeadlineExpansions(template);
    if (expansions.length < 2) {
      throw new ValidationError(
        path,
        "headline template must produce more than one grammatical expansion",
      );
    }
    if (!headlineTemplateExpansionsAreGrammatical(template, packStub)) {
      throw new ValidationError(
        path,
        "every headline expansion must satisfy caption grammar",
      );
    }
    for (const expansion of expansions) {
      if (!captionMatchesGrammar(expansion, packStub)) {
        throw new ValidationError(path, "ungrammatical headline expansion");
      }
    }
    return template;
  });
  uniqueIds(
    "headlineTemplates",
    templates.map((t) => t.id),
  );
  // Every expansion must map to exactly one template+selection (no template IDs in EditGraph).
  const seen = new Map<string, string>();
  for (const template of templates) {
    for (const choice of allHeadlineExpansionChoices(template)) {
      const key = choice.tokenIds.join("\u0001");
      const prior = seen.get(key);
      if (prior) {
        throw new ValidationError(
          "headlineTemplates",
          `duplicate expansion shared by "${prior}" and "${template.id}"`,
        );
      }
      seen.set(key, template.id);
    }
  }
  return templates;
}

function requireTags(path: string, raw: unknown): string[] {
  return requireArray(path, raw).map((tag, i) =>
    requireNonEmptyString(`${path}[${i}]`, tag),
  );
}

function validateLookTreatments(raw: unknown): LookTreatment[] {
  if (raw === undefined) return [];
  const list = requireArray("lookTreatments", raw);
  const treatments = list.map((item, i) => {
    const path = `lookTreatments[${i}]`;
    const t = requireObject(path, item);
    return {
      id: requireNonEmptyString(`${path}.id`, t.id),
      label: requireNonEmptyString(`${path}.label`, t.label),
      tags: requireTags(`${path}.tags`, t.tags),
      contrastMilli: requireMilli(`${path}.contrastMilli`, t.contrastMilli, 0, 2000),
      saturationMilli: requireMilli(`${path}.saturationMilli`, t.saturationMilli, 0, 2000),
      warmthMilli: requireMilli(`${path}.warmthMilli`, t.warmthMilli, 0, 2000),
      vignetteMilli: requireMilli(`${path}.vignetteMilli`, t.vignetteMilli, 0, 1000),
      intensitySupported: requireBoolean(`${path}.intensitySupported`, t.intensitySupported),
      defaultIntensityMilli: requireMilli(
        `${path}.defaultIntensityMilli`,
        t.defaultIntensityMilli,
        0,
        1000,
      ),
      presentationReachMilli: requireMilli(
        `${path}.presentationReachMilli`,
        t.presentationReachMilli,
        -200,
        200,
      ),
      productionMilli: requireMilli(`${path}.productionMilli`, t.productionMilli, 0, 100),
    };
  });
  uniqueIds(
    "lookTreatments",
    treatments.map((t) => t.id),
  );
  return treatments;
}

function validateCameraMotions(raw: unknown): CameraMotionPreset[] {
  if (raw === undefined) return [];
  const list = requireArray("cameraMotions", raw);
  const motions = list.map((item, i) => {
    const path = `cameraMotions[${i}]`;
    const m = requireObject(path, item);
    return {
      id: requireNonEmptyString(`${path}.id`, m.id),
      label: requireNonEmptyString(`${path}.label`, m.label),
      tags: requireTags(`${path}.tags`, m.tags),
      amplitudeMilli: requireMilli(`${path}.amplitudeMilli`, m.amplitudeMilli, 0, 1000),
      frequencyMilli: requireMilli(`${path}.frequencyMilli`, m.frequencyMilli, 0, 2000),
      zoomDeltaMilli: requireMilli(`${path}.zoomDeltaMilli`, m.zoomDeltaMilli, -500, 500),
      presentationReachMilli: requireMilli(
        `${path}.presentationReachMilli`,
        m.presentationReachMilli,
        -200,
        200,
      ),
      productionMilli: requireMilli(`${path}.productionMilli`, m.productionMilli, 0, 100),
    };
  });
  uniqueIds(
    "cameraMotions",
    motions.map((m) => m.id),
  );
  return motions;
}

function validateTransitionPresets(raw: unknown): TransitionPreset[] {
  if (raw === undefined) return [];
  const list = requireArray("transitionPresets", raw);
  const presets = list.map((item, i) => {
    const path = `transitionPresets[${i}]`;
    const t = requireObject(path, item);
    const maxDurationTicks = requireInt(`${path}.maxDurationTicks`, t.maxDurationTicks, 1);
    const defaultDurationTicks = requireInt(
      `${path}.defaultDurationTicks`,
      t.defaultDurationTicks,
      1,
      maxDurationTicks,
    );
    return {
      id: requireNonEmptyString(`${path}.id`, t.id),
      label: requireNonEmptyString(`${path}.label`, t.label),
      tags: requireTags(`${path}.tags`, t.tags),
      maxDurationTicks,
      defaultDurationTicks,
      presentationReachMilli: requireMilli(
        `${path}.presentationReachMilli`,
        t.presentationReachMilli,
        -200,
        200,
      ),
      productionMilli: requireMilli(`${path}.productionMilli`, t.productionMilli, 0, 100),
    };
  });
  uniqueIds(
    "transitionPresets",
    presets.map((p) => p.id),
  );
  return presets;
}

function validateStickerOptions(
  raw: unknown,
  beatIds: Set<string>,
  assetIds: Set<string>,
  assets: SourcePack["assets"],
): StickerOption[] {
  if (raw === undefined) return [];
  const list = requireArray("stickerOptions", raw);
  const shapes = new Set([
    "badge",
    "burst",
    "arrow",
    "underline",
    "frame",
    "dot",
    "star",
    "text",
  ]);
  const stickers = list.map((item, i) => {
    const path = `stickerOptions[${i}]`;
    const s = requireObject(path, item);
    const renderObj = requireObject(`${path}.render`, s.render);
    const renderKind = requireNonEmptyString(`${path}.render.kind`, renderObj.kind);
    let render: StickerOption["render"];
    if (renderKind === "procedural") {
      const shape = requireNonEmptyString(`${path}.render.shape`, renderObj.shape);
      if (!shapes.has(shape)) {
        throw new ValidationError(`${path}.render.shape`, `invalid shape "${shape}"`);
      }
      render = {
        kind: "procedural",
        shape: shape as Extract<StickerOption["render"], { kind: "procedural" }>["shape"],
        colorTag: requireNonEmptyString(`${path}.render.colorTag`, renderObj.colorTag),
      };
      if (renderObj.text !== undefined) {
        render.text = requireNonEmptyString(`${path}.render.text`, renderObj.text);
      }
    } else if (renderKind === "asset") {
      const assetId = requireNonEmptyString(`${path}.render.assetId`, renderObj.assetId);
      if (!assetIds.has(assetId)) {
        throw new ValidationError(`${path}.render.assetId`, `unknown asset "${assetId}"`);
      }
      const asset = assets.find((a) => a.id === assetId);
      if (asset && asset.kind !== "sticker" && asset.kind !== "visual") {
        throw new ValidationError(
          `${path}.render.assetId`,
          'sticker asset must have kind "sticker" or "visual"',
        );
      }
      render = { kind: "asset", assetId };
    } else {
      throw new ValidationError(`${path}.render.kind`, `invalid kind "${renderKind}"`);
    }
    const sticker: StickerOption = {
      id: requireNonEmptyString(`${path}.id`, s.id),
      label: requireNonEmptyString(`${path}.label`, s.label),
      tags: requireTags(`${path}.tags`, s.tags),
      render,
      defaultScaleMilli: requireMilli(`${path}.defaultScaleMilli`, s.defaultScaleMilli, 100, 3000),
      defaultRotationMilli: requireMilli(
        `${path}.defaultRotationMilli`,
        s.defaultRotationMilli,
        0,
        360000,
      ),
      presentationReachMilli: requireMilli(
        `${path}.presentationReachMilli`,
        s.presentationReachMilli,
        -200,
        200,
      ),
      productionMilli: requireMilli(`${path}.productionMilli`, s.productionMilli, 0, 100),
    };
    if (s.allowedBeatIds !== undefined) {
      const allowed = requireStringArray(`${path}.allowedBeatIds`, s.allowedBeatIds);
      for (const beatId of allowed) {
        if (!beatIds.has(beatId)) {
          throw new ValidationError(`${path}.allowedBeatIds`, `unknown beat "${beatId}"`);
        }
      }
      sticker.allowedBeatIds = allowed;
    }
    return sticker;
  });
  uniqueIds(
    "stickerOptions",
    stickers.map((s) => s.id),
  );
  return stickers;
}

function validateSfxOptions(
  raw: unknown,
  assetIds: Set<string>,
  assets: SourcePack["assets"],
  sourceEndTick: number,
): SfxOption[] {
  if (raw === undefined) return [];
  const list = requireArray("sfxOptions", raw);
  const seenAssetIds = new Set<string>();
  const options = list.map((item, i) => {
    const path = `sfxOptions[${i}]`;
    const o = requireObject(path, item);
    const defaultAtTick = requireInt(`${path}.defaultAtTick`, o.defaultAtTick, 0);
    const defaultDurationTicks = requireInt(
      `${path}.defaultDurationTicks`,
      o.defaultDurationTicks,
      1,
    );
    if (defaultAtTick > sourceEndTick) {
      throw new ValidationError(
        `${path}.defaultAtTick`,
        `must lie within source bounds [0, ${sourceEndTick}]`,
      );
    }
    if (defaultAtTick + defaultDurationTicks > sourceEndTick) {
      throw new ValidationError(
        `${path}.defaultDurationTicks`,
        `default window must lie within source bounds [0, ${sourceEndTick}]`,
      );
    }
    const option: SfxOption = {
      id: requireNonEmptyString(`${path}.id`, o.id),
      label: requireNonEmptyString(`${path}.label`, o.label),
      tags: requireTags(`${path}.tags`, o.tags),
      defaultAtTick,
      defaultDurationTicks,
      defaultVolumeMilli: requireMilli(
        `${path}.defaultVolumeMilli`,
        o.defaultVolumeMilli,
        0,
        1000,
      ),
      presentationReachMilli: requireMilli(
        `${path}.presentationReachMilli`,
        o.presentationReachMilli,
        -200,
        200,
      ),
      productionMilli: requireMilli(`${path}.productionMilli`, o.productionMilli, 0, 100),
    };
    if (o.assetId !== undefined) {
      const assetId = requireNonEmptyString(`${path}.assetId`, o.assetId);
      if (!assetIds.has(assetId)) {
        throw new ValidationError(`${path}.assetId`, `unknown asset "${assetId}"`);
      }
      const asset = assets.find((a) => a.id === assetId);
      if (asset && asset.kind !== "audio") {
        throw new ValidationError(`${path}.assetId`, 'sfx asset must have kind "audio"');
      }
      if (seenAssetIds.has(assetId)) {
        throw new ValidationError(
          `${path}.assetId`,
          `duplicate non-null assetId "${assetId}" across sfxOptions`,
        );
      }
      seenAssetIds.add(assetId);
      option.assetId = assetId;
    }
    return option;
  });
  uniqueIds(
    "sfxOptions",
    options.map((o) => o.id),
  );
  return options;
}
